/**
 * Incremental wall topology updates — reshape or remove walls in the live 3D scene
 * without rebuilding the whole room (used by the 2D plan editor).
 */

import { matchWallForSegment, stripStructureMeshes } from "./opening_patch.js";
import { expandWallsFromModel } from "./corner_fillet.js";
import {
  applyBoxFloorMesh,
  applyPolygonFloorMesh,
  createFloorSlabEntity,
  normalizeFloorPolygon,
  outsetFloorPolygon,
  roomFloorPolygon,
} from "./floor_mesh.js";
import { createSurfaceLibrary } from "./surface_library.js";
import { CEIL_SLAB, SLAB_WRAP, ceilingEnvelopeSlab, floorEnvelopeSlab } from "./room_builder.js";

const round3 = (v) => Math.round(v * 1000) / 1000;

/** Inward-facing normal for a wall segment (XZ plane). */
function inwardNormal(ax, az, bx, bz, room) {
  const len = Math.hypot(bx - ax, bz - az) || 1;
  let nx = -(bz - az) / len;
  let nz = (bx - ax) / len;
  const b = room?.bounds;
  if (b) {
    const cx = (b.min[0] + b.max[0]) / 2;
    const cz = (b.min[2] + b.max[2]) / 2;
    const mx = (ax + bx) / 2;
    const mz = (az + bz) / 2;
    if ((cx - mx) * nx + (cz - mz) * nz < 0) {
      nx = -nx;
      nz = -nz;
    }
  }
  return [round3(nx), round3(nz)];
}

/** Build exterior wall segments that follow a floor polygon ring (CCW). */
export function wallsFromFloorPolygon(polygon, room, opts = {}) {
  const poly = normalizeFloorPolygon(polygon);
  if (poly.length < 3) return [];
  const height = opts.height ?? room?.dimensions?.height ?? 2.8;
  const thickness = opts.thickness ?? 0.15;
  const walls = [];
  for (let i = 0; i < poly.length; i++) {
    const [ax, az] = poly[i];
    const [bx, bz] = poly[(i + 1) % poly.length];
    if (Math.hypot(bx - ax, bz - az) < 0.02) continue;
    walls.push({
      id: `w_floor_${i}`,
      start: [round3(ax), round3(az)],
      end: [round3(bx), round3(bz)],
      height,
      thickness,
      normal: inwardNormal(ax, az, bx, bz, room),
    });
  }
  return walls;
}

/**
 * Replace live room walls with segments that match the floor outline.
 * @returns {{ upsert: object[], remove: string[] }}
 */
export function computeFloorWallSyncPatch(room, polygon) {
  if (!room) return { upsert: [], remove: [] };
  const next = wallsFromFloorPolygon(polygon, room);
  const active = new Set(next.map((w) => w.id));
  const remove = (room.walls ?? []).map((w) => w.id).filter((id) => !active.has(id));
  return { upsert: next, remove };
}

/** Apply floor polygon walls to room data and return a topology patch. */
export function syncRoomWallsToFloorPolygon(room, polygon) {
  const patch = computeFloorWallSyncPatch(room, polygon);
  room.walls = patch.upsert;
  recomputeRoomEnvelope(room);
  return patch;
}

/**
 * @param {object} edge
 * @param {object} model plan editor model (nodes map + edges)
 * @param {object} room live scene room
 * @param {(px:number, py:number) => { x:number, z:number }} pxToWorld
 */
export function wallSpecFromEdge(edge, model, room, pxToWorld) {
  const A = model.nodes.get(String(edge.a));
  const B = model.nodes.get(String(edge.b));
  if (!A || !B) return null;

  const wa = pxToWorld(A.px, A.py);
  const wb = pxToWorld(B.px, B.py);
  const ax = round3(wa.x);
  const az = round3(wa.z);
  const bx = round3(wb.x);
  const bz = round3(wb.z);
  if (Math.hypot(bx - ax, bz - az) < 0.02) return null;

  let existing = edge.sceneWallId ? room.walls?.find((w) => w.id === edge.sceneWallId) : null;
  if (!existing) {
    existing = matchWallForSegment(room, ax, az, bx, bz);
    if (existing) edge.sceneWallId = existing.id;
  }

  const id = existing?.id ?? edge.sceneWallId ?? `w_pe_${edge.id}`;
  edge.sceneWallId = id;
  const height = room.dimensions?.height ?? model.height ?? 2.8;
  const isExterior = /^w_(south|north|east|west|ext)/.test(id) || Boolean(existing?.view);
  const thickness = existing?.thickness ?? (isExterior ? 0.15 : 0.1);

  return {
    id,
    start: [ax, az],
    end: [bx, bz],
    height,
    thickness,
    normal: inwardNormal(ax, az, bx, bz, room),
    ...(existing?.view ? { view: true, viewLength: existing.viewLength } : {}),
  };
}

/**
 * Diff the plan-editor graph against the live room walls.
 * @returns {{ upsert: object[], remove: string[] }}
 */
export function computeTopologyPatch(model, room, pxToWorld) {
  if (!model || !room) return { upsert: [], remove: [] };
  const upsert = expandWallsFromModel(model, room, pxToWorld);
  const active = new Set(upsert.map((w) => w.id));
  const remove = (room.walls ?? []).map((w) => w.id).filter((id) => !active.has(id));
  return { upsert, remove };
}

/** Recompute room.bounds and dimensions from wall endpoints (and floor polygon when present). */
export function recomputeRoomEnvelope(room) {
  const walls = room?.walls ?? [];
  if (!walls.length) return;
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  const floorPoly = roomFloorPolygon(room);
  if (floorPoly?.length >= 3) {
    for (const [x, z] of floorPoly) {
      minX = Math.min(minX, x);
      minZ = Math.min(minZ, z);
      maxX = Math.max(maxX, x);
      maxZ = Math.max(maxZ, z);
    }
  }
  for (const w of walls) {
    for (const [x, z] of [w.start, w.end]) {
      minX = Math.min(minX, x);
      minZ = Math.min(minZ, z);
      maxX = Math.max(maxX, x);
      maxZ = Math.max(maxZ, z);
    }
  }
  const height = room.dimensions?.height ?? 2.8;
  room.bounds = { min: [minX, 0, minZ], max: [maxX, height, maxZ] };
  room.dimensions = { width: maxX - minX, depth: maxZ - minZ, height };
}

/** Set room.bounds from a floor polygon ring (world XZ). */
function envelopeFromFloorPolygon(room) {
  const floorPoly = roomFloorPolygon(room);
  if (!floorPoly?.length) return false;
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const [x, z] of floorPoly) {
    if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
    minX = Math.min(minX, x);
    minZ = Math.min(minZ, z);
    maxX = Math.max(maxX, x);
    maxZ = Math.max(maxZ, z);
  }
  if (!Number.isFinite(minX)) return false;
  const height = room.dimensions?.height ?? 2.8;
  room.bounds = { min: [minX, 0, minZ], max: [maxX, height, maxZ] };
  room.dimensions = { width: maxX - minX, depth: maxZ - minZ, height };
  return true;
}

/** Apply a floor polygon to room data (floor-only mode). */
export function patchFloorPolygon(room, polygon) {
  if (!room || !Array.isArray(polygon) || polygon.length < 3) return;
  const primary = room.floors?.[0] ?? { id: "f_main", y: 0, polygon: [] };
  primary.polygon = polygon;
  if (!room.floors?.length) room.floors = [primary];
  else room.floors[0] = primary;
  room.floorPolygon = polygon;
}

/**
 * Cleared-apartment shell: drop all structure meshes and show only the floor slab.
 * @param {object} room
 * @param {pc.Entity} roomRoot
 * @param {pc.AppBase} app
 */
export function applyFloorOnlyShell(room, roomRoot, app) {
  if (room) {
    room.walls = [];
    room.openings = [];
    room.rooms = [];
    envelopeFromFloorPolygon(room);
  }
  stripStructureMeshes(roomRoot);
  patchRoomEnvelope(room, roomRoot, app);
}

/** Wood floor material cached on the room root (survives live envelope patches). */
function resolveFloorMaterial(roomRoot, room, app, forceFresh = false) {
  if (!forceFresh) {
    const cached = roomRoot?._aspectFloorMat;
    if (cached?.diffuseMap) return cached;
  }
  if (!app || !room?.bounds) return roomRoot?._aspectFloorMat ?? null;
  const { min, max } = room.bounds;
  const width = max[0] - min[0];
  const depth = max[2] - min[2];
  const surf = createSurfaceLibrary(app, {
    width,
    depth,
    height: room.dimensions?.height ?? 2.8,
  });
  if (roomRoot) roomRoot._aspectFloorMat = surf.floorWood;
  return surf.floorWood;
}

/** Replace the floor entity so box→polygon transitions after clear always render. */
function rebuildFloorEntity(roomRoot, room, app, floorY) {
  if (!roomRoot || !app || !room?.bounds) return;
  const { min, max } = room.bounds;
  const width = max[0] - min[0];
  const depth = max[2] - min[2];
  if (!(width > 0) || !(depth > 0)) return;
  const mat = resolveFloorMaterial(roomRoot, room, app, true);
  if (!mat) return;
  const existing = roomRoot.findByName?.("floor") ?? roomRoot.children.find((c) => c.name === "floor");
  if (existing) existing.destroy();

  const floor = createFloorSlabEntity(app, "floor", room, floorY, mat);
  if (!floor) return;
  roomRoot.addChild(floor);
}

/** Resize floor/ceiling slabs when the envelope changes. */
export function patchRoomEnvelope(room, roomRoot, app = null) {
  if (!roomRoot || !room?.bounds) return;
  const floorOnly = !(room.walls?.length);
  if (!floorOnly) recomputeRoomEnvelope(room);
  const { min, max } = room.bounds;
  const width = max[0] - min[0];
  const depth = max[2] - min[2];
  if (floorOnly && room.floors?.[0]?.polygon?.length >= 3) {
    patchFloorPolygon(room, room.floors[0].polygon);
  }
  const floorY = room.floorY ?? 0;
  const ceilH = room.dimensions?.height ?? 2.8;
  const floorCount = Math.min(10, Math.max(1, Math.round(Number(room.floorCount) || 1)));
  const activeFloor = Math.min(floorCount, Math.max(1, Math.round(Number(room.activeFloor) || 1)));
  const floorPoly = roomFloorPolygon(room);
  const floorMat = resolveFloorMaterial(roomRoot, room, app);
  if (floorOnly && app) {
    rebuildFloorEntity(roomRoot, room, app, floorY);
  }
  for (const child of roomRoot.children) {
    if (child.name === "floor") {
      if (floorOnly) {
        if (child.render) child.render.enabled = true;
        continue;
      }
      const wrap = floorEnvelopeSlab(min, max);
      const wrappedPoly = floorPoly?.length >= 3 ? outsetFloorPolygon(floorPoly, SLAB_WRAP) : null;
      if (wrappedPoly && app && applyPolygonFloorMesh(child, app, wrappedPoly, floorY, floorMat)) {
        // polygon mesh — verts are world XZ, outset past the walls
      } else if (applyBoxFloorMesh(child, wrap.cx, wrap.cz, wrap.w, wrap.d, floorY, floorMat)) {
        // reset asset mesh back to a wrapped box
      } else {
        child.setLocalEulerAngles(0, 0, 0);
        child.setLocalScale(wrap.w, 0.05, wrap.d);
        child.setLocalPosition(wrap.cx, floorY - 0.025, wrap.cz);
      }
      if (child.render) child.render.enabled = true;
    } else if (child.name === "grass-ground") {
      const GRASS_MARGIN = 5;
      const grassW = width + GRASS_MARGIN * 2;
      const grassD = depth + GRASS_MARGIN * 2;
      child.setLocalScale(grassW, 0.08, grassD);
      child.setLocalPosition((min[0] + max[0]) / 2, -0.22, (min[2] + max[2]) / 2);
      if (child.render) child.render.enabled = true;
    } else if (child.name === "ceiling") {
      if (child.render) child.render.enabled = !floorOnly;
      if (!floorOnly) {
        const ceil = ceilingEnvelopeSlab(min, max);
        child.setLocalScale(ceil.w, CEIL_SLAB, ceil.d);
        child.setLocalPosition(ceil.cx, floorY + ceilH + CEIL_SLAB / 2, ceil.cz);
      }
    } else if (child.name === "building-roof") {
      if (child.render) child.render.enabled = !floorOnly && floorCount > 1 && activeFloor < floorCount;
      if (!floorOnly && floorCount > 1 && activeFloor < floorCount) {
        let shellY = (floorCount - 1) * ceilH;
        if (floorCount > activeFloor) shellY += CEIL_SLAB + 0.12;
        const roof = ceilingEnvelopeSlab(min, max);
        child.setLocalScale(roof.w, CEIL_SLAB, roof.d);
        child.setLocalPosition(roof.cx, shellY + ceilH + CEIL_SLAB / 2, roof.cz);
      }
    }
  }
}
